/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { selectAuthUser } from 'modules/common/auth/selectors';
import { useSelector } from 'react-redux';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Box,
  TableSortLabel,
  CircularProgress,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import NoDataDialog from 'modules/dashboard/components/tab-container/export-visual-data/components/no-data';
import { selectHotelName } from 'modules/dashboard/selectors';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  BACKGROUND_STYLE,
  HEADER_CONTAINER_STYLE,
  HEADER_ROW_STYLE,
  HEADER_STYLE,
  LOADING_STYLE,
  NO_RESULT_STYLE,
  PRIMARY_ROW_COLOR,
  PRIMARY_TEXT_COLOR,
  SECONDARY_ROW_COLOR,
  SECONDARY_TEXT_COLOR,
  SORT_STYLE,
  TABLE_STYLE,
  TOTAL_CELL_STYLE,
  TOTAL_ROW_STYLE,
} from './style';
import { DEFAULT_CELL_ALIGN, HEADER_ALIGN, monthMap, NO_RESULT_TEXT } from './constant';
import generateExcel, { buildExcelContent } from './generate-excel';
import { ExpandableBodyRow } from './expandable-table-row';
import {
  areAllValuesZero,
  flattenColumns,
  flattenData,
  formatData,
  getHeaderRows,
  getRenderedColumnCount,
  shouldShowNoResult,
} from './function';
import { BodyRow } from './table-row';

// Recursively calculates column span for nested headers.
const getColSpan = (col) =>
  col?.children ? col?.children.reduce((span, child) => span + getColSpan(child), 0) : 1;

/**
 * Formats cell value for display based on type.
 * Returns empty string if value is null or undefined.
 */
const renderCellValue = (value, type) =>
  value !== null && value !== undefined ? formatData(value, type || typeof value) : '';

/**
 * Renders all data rows in the table body.
 * Alternates row colors and applies number formatting & color logic.
 */
const renderBodyRows = (
  rowData,
  flatColumns,
  expandedRowIds,
  setExpandedRowIds,
  primaryRowColor,
  secondaryRowColor,
  primaryTextColor,
  secondaryTextColor,
  rowOnClick,
  isDataLoading = false,
  customRowStyle = {},
  expandableKeys = [],
  isDrillDown = false
) =>
  rowData?.map((row, index) =>
    expandableKeys?.length ? (
      <ExpandableBodyRow
        key={row?.id || index}
        row={row}
        rowIndex={index}
        expandedRowIds={expandedRowIds}
        setExpandedRowIds={setExpandedRowIds}
        flatColumns={flatColumns}
        primaryRowColor={primaryRowColor}
        secondaryRowColor={secondaryRowColor}
        primaryTextColor={primaryTextColor}
        secondaryTextColor={secondaryTextColor}
        customRowStyle={customRowStyle}
        renderCellValue={renderCellValue}
        rowOnClick={rowOnClick}
        expandableKeys={expandableKeys}
        isDrillDown={isDrillDown}
        isDataLoading={isDataLoading}
      />
    ) : (
      <BodyRow
        key={row?.id || index}
        row={row}
        rowIndex={index}
        expandedRowIds={expandedRowIds}
        setExpandedRowIds={setExpandedRowIds}
        flatColumns={flatColumns}
        primaryRowColor={primaryRowColor}
        secondaryRowColor={secondaryRowColor}
        primaryTextColor={primaryTextColor}
        secondaryTextColor={secondaryTextColor}
        customRowStyle={customRowStyle}
        renderCellValue={renderCellValue}
        rowOnClick={rowOnClick}
      />
    )
  );

/**
 * Renders a summary aggregation row at the bottom of the table body.
 * Uses same structure as normal rows with conditional coloring.
 */
const renderAggregationRow = (
  totalData,
  flatColumns,
  dataLength,
  primaryTextColor,
  secondaryTextColor,
  customRowStyle = {}
) => (
  <TableRow sx={TOTAL_ROW_STYLE}>
    {flatColumns.map((col) => (
      <TableCell
        key={`${col?.id}_${col?.label}_total`}
        align={col?.align || DEFAULT_CELL_ALIGN}
        sx={{
          ...TOTAL_CELL_STYLE,
          ...customRowStyle,
          bgcolor: dataLength % 2 === 0 ? PRIMARY_ROW_COLOR : SECONDARY_ROW_COLOR,
          color:
            parseFloat(totalData?.[col?.id]) < 0
              ? secondaryTextColor || SECONDARY_TEXT_COLOR
              : primaryTextColor || PRIMARY_TEXT_COLOR,
        }}
      >
        {renderCellValue(totalData?.[col?.id])}
      </TableCell>
    ))}
  </TableRow>
);

const findNotNullColumns = (rows) => {
  if (!rows?.length) return [];

  return Object.keys(rows[0])
    .filter((key) => rows?.some((row) => row[key] !== null && row[key] !== undefined))
    .map((id) => id?.replace(/\u200B/g, '').trim());
};

const removeColumnsFromSchema = (schema, columnIds = []) =>
  schema
    .map((parent) => ({
      ...parent,
      children: parent.children?.filter((child) => columnIds?.find((id) => id === child.id)) || [],
    }))
    .filter((parent) => parent.children.length > 0);

/**
 * Renders all header rows in the table.
 * Applies correct rowspan/colspan and sticky styling per level.
 * returns table header component with sorting functionality
 */
const EnhancedTableHead = (props) => {
  const { headerRows, order, orderBy, onRequestSort, customHeaderStyle = {} } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };
  return headerRows.map((row, rowIndex) => (
    <TableRow key={row?.id || rowIndex} style={HEADER_ROW_STYLE(rowIndex)}>
      {row.map((col) => (
        <TableCell
          key={`${col.id}_${col.label}`}
          colSpan={getColSpan(col)}
          rowSpan={col.children ? 1 : headerRows.length - rowIndex}
          align={HEADER_ALIGN}
          sx={{ ...HEADER_STYLE, ...customHeaderStyle }}
          sortDirection={orderBy === col.id ? order : false}
        >
          {col?.sorting === 'disabled' ? (
            col.label
          ) : (
            <TableSortLabel
              active={orderBy === col.id}
              direction={orderBy === col.id ? order : 'asc'}
              onClick={createSortHandler(col.id)}
              sx={SORT_STYLE}
              IconComponent={ArrowDropDownIcon}
            >
              {col.label}
              {orderBy === col.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          )}
        </TableCell>
      ))}
    </TableRow>
  ));
};

/**
 * MuiTabular table component
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Array<Object>} props.columns - An array of column definitions. Each column may contain nested `children` for grouped headers.
 * @param {Array<Object>} props.data - An array of row data. The last element is treated as the "total" row.
 * @param {Object} [props.headerFiltersInfo] - Optional metadata to render a filter header section.
 * @param {string} [props.headerFiltersInfo.title] - Title to be displayed in the filter header.
 * @param {string} [props.headerFiltersInfo.hotelName] - Hotel name to display alongside the title.
 * @param {Array<Object>} [props.headerFiltersInfo.sections] - Additional filter sections for the filter header.
 * @param {React.ReactNode} [props.headerComponent] - Optional custom header component to render above the table.
 * @param {string} props.fileName - Name of the file to be exported (e.g., for CSV/Excel export).
 * @param {boolean} [props.isAggregate=true] - Whether to display the aggregate (total) row.
 * @param {boolean} [props.isProcessing] - Flag indicating whether the table is in a loading or processing state.
 * @param {Function} [props.setIsProcessing] - Callback function to update the processing state.
 * @param {Object} [props.customRowStyle={}] - Inline style overrides applied to table rows.
 * @param {Object} [props.customHeaderStyle={}] - Inline style overrides applied to table headers.
 * @param {Object} [props.customBackgroundStyle={}] - Inline style overrides applied to the table background.
 * @param {string} [props.primaryRowColor=PRIMARY_ROW_COLOR] - Background color for primary rows.
 * @param {string} [props.secondaryRowColor=SECONDARY_ROW_COLOR] - Background color for secondary rows.
 * @param {string} [props.primaryTextColor=PRIMARY_TEXT_COLOR] - Text color for primary rows.
 * @param {string} [props.secondaryTextColor=SECONDARY_TEXT_COLOR] - Text color for secondary rows.
 *
 * @returns {JSX.Element} The rendered MUI Tabular table component.
 */
const MuiTabular = forwardRef(
  (
    {
      columns,
      data,
      headerFiltersInfo,
      headerComponent,
      fileName,
      isAggregate = true,
      isProcessing,
      setIsProcessing,
      isLoadingData,
      rowOnClick = () => {},
      removeEmptyColumns = false,
      customRowStyle = {},
      customHeaderStyle = {},
      customTableStyle = {},
      customBackgroundStyle = {},
      customAggregationRowStyle = {},
      primaryRowColor = PRIMARY_ROW_COLOR,
      secondaryRowColor = SECONDARY_ROW_COLOR,
      primaryTextColor = PRIMARY_TEXT_COLOR,
      secondaryTextColor = SECONDARY_TEXT_COLOR,
      expandableKeys = [],
      isDrillDown = false,
      defaultOrder = 'asc',
      defaultOrderBy = null,
      onAllRowsCollapse = () => {},
    },
    ref
  ) => {
    const currentUser = useSelector(selectAuthUser);
    const hotel = useSelector(selectHotelName);
    const [expandedRowIds, setExpandedRowIds] = useState(new Set());
    //
    const [message, setMessage] = useState('');
    const [isDialog, setIsDialog] = useState(false);
    // const [sortField, setSortField] = useState(null);
    // const [sortDirection, setSortDirection] = useState('asc');
    const [order, setOrder] = useState(defaultOrder);
    const [orderBy, setOrderBy] = useState(defaultOrderBy);
    //
    const tableColumns = useMemo(() => {
      if (!removeEmptyColumns) return columns;

      const availableKeys = findNotNullColumns(data);
      if (availableKeys?.length > 0) {
        return removeColumnsFromSchema(columns, availableKeys);
      }
      return columns;
    }, [columns, data, removeEmptyColumns]);
    //
    const { rowData, aggregationData } = useMemo(() => {
      if (data && data.length > 1 && isAggregate) {
        const total = data[data.length - 1];
        const rows = data.slice(0, -1);
        return { rowData: rows, aggregationData: total };
      }
      return { rowData: data, aggregationData: [] };
    }, [data, isAggregate]);
    //
    const headerRows = useMemo(() => getHeaderRows(tableColumns), [tableColumns]);
    const flatColumns = useMemo(() => flattenColumns(tableColumns), [tableColumns]);
    // full table width for the loading and no-result cells, including empty column groups
    const bodyColSpan = useMemo(() => getRenderedColumnCount(tableColumns), [tableColumns]);
    // sort based on column and order
    const handleRequestSort = (event, property) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    };
    // transform data to support sort
    const parseValue = (val, column) => {
      if (val == null) return 0;

      if (typeof val === 'string') {
        const trimmed = val.trim();

        // Handle percentages
        if (trimmed.endsWith('%')) {
          return parseFloat(trimmed.replace('%', ''));
        }

        const numeric = trimmed.replace(/,/g, '');
        // eslint-disable-next-line no-restricted-globals
        if (!isNaN(numeric) && numeric !== '') {
          return Number(numeric);
        }

        // Handle months
        if (column === 'month' && monthMap[trimmed]) {
          return monthMap[trimmed];
        }
      }
      if (typeof val === 'number') {
        return val;
      }

      return val;
    };
    // compare values for sorting
    const descendingComparator = (a, b, orderBy2) => {
      const valA = parseValue(a[orderBy2], orderBy2);
      const valB = parseValue(b[orderBy2], orderBy2);

      if (valB < valA) return -1;
      if (valB > valA) return 1;
      return 0;
    };
    // sort data based on selected column and order
    const getComparator = (order1, orderBy1) =>
      order1 === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy1)
        : (a, b) => -descendingComparator(a, b, orderBy1);
    //
    const visibleRows = useMemo(
      () => [...rowData].sort(getComparator(order, orderBy)),
      [order, orderBy, rowData]
    );
    useEffect(() => {
      if (!expandedRowIds.size) {
        onAllRowsCollapse();
      }
    }, [expandedRowIds]);
    //
    useEffect(() => {
      // start exporting the excel file
      if (isProcessing && setIsProcessing) {
        try {
          if (!headerFiltersInfo.hotelName) {
            headerFiltersInfo.hotelName = hotel?.label;
          }
          if (!visibleRows?.length) {
            setIsDialog(true);
            setMessage('No data available to export');
          } else {
            generateExcel(
              fileName,
              headerFiltersInfo,
              headerRows,
              flatColumns,
              flattenData(visibleRows),
              currentUser
            );
          }
        } catch {
          setIsDialog(true);
          setMessage('No data available to export');
        }
        setIsProcessing(false);
      }
    }, [isProcessing]);
    //
    const getExcelData = () => {
      const { csv } = buildExcelContent({
        headerFiltersInfo,
        headerRows,
        flatColumns,
        rowData: flattenData(visibleRows),
        user: currentUser,
        isInterpreterAgent: true,
      });
      return csv;
    };
    //
    const handleExpandAll = () => {
      const allRowIds = new Set(visibleRows.map((_, index) => index));
      setExpandedRowIds(allRowIds);
    }
    const handleCollapseAll = () => {
      setExpandedRowIds(new Set());
    }
    // EXPOSE METHODS TO PARENT
    useImperativeHandle(ref, () => ({
      getExcelData,
      expandedRowIds,
      setExpandedRowIds,
      handleExpandAll,
      handleCollapseAll
    }));

    //
    return (
      <Box sx={{ ...BACKGROUND_STYLE, ...customBackgroundStyle }}>
        <Box sx={HEADER_CONTAINER_STYLE}>{headerComponent}</Box>
        <TableContainer component={Paper} sx={{ ...TABLE_STYLE, ...customTableStyle }}>
          {/* relative so the absolutely positioned loading cell is centered across the table */}
          <Table stickyHeader sx={{ position: 'relative' }}>
            <TableHead>
              <EnhancedTableHead
                headerRows={headerRows}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                customHeaderStyle={customHeaderStyle}
              />
            </TableHead>
            <TableBody>
              {isLoadingData && (
                <TableRow>
                  <TableCell colSpan={bodyColSpan} sx={LOADING_STYLE} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              )}
              {shouldShowNoResult(visibleRows, isLoadingData) ? (
                <TableRow>
                  <TableCell colSpan={bodyColSpan} sx={NO_RESULT_STYLE} align="center">
                    {NO_RESULT_TEXT}
                  </TableCell>
                </TableRow>
              ) : (
                renderBodyRows(
                  visibleRows,
                  flatColumns,
                  expandedRowIds,
                  setExpandedRowIds,
                  primaryRowColor,
                  secondaryRowColor,
                  primaryTextColor,
                  secondaryTextColor,
                  rowOnClick,
                  isLoadingData,
                  customRowStyle,
                  expandableKeys,
                  isDrillDown
                )
              )}
              {isAggregate &&
                !areAllValuesZero(visibleRows) &&
                renderAggregationRow(
                  aggregationData,
                  flatColumns,
                  data.length,
                  primaryTextColor,
                  secondaryTextColor,
                  customAggregationRowStyle
                )}
            </TableBody>
          </Table>
        </TableContainer>
        <NoDataDialog open={isDialog} onClose={() => setIsDialog(false)} message={message} />
      </Box>
    );
  }
);
//
export default MuiTabular;
